import { useNavigate } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import { Reveal, SectionTag, MaskedLines, EASE } from "@/components/Reveal";
import { GoldDot, DrawLine, SvgLabel, TravelDot } from "@/components/InsightVisuals";
import { CONTACT } from "@/data/content";

/* enquiry-routing diagram: Your Enquiry → channels → Logibyte → Next Step */
const ConnectVisual = ({ reduce }) => {
  const channels = [
    { label: "Technology", x: 58 },
    { label: "Business Systems", x: 164 },
    { label: "Partnerships", x: 278 },
    { label: "Corporate", x: 382 },
  ];
  return (
    <div data-testid="connect-visual" className="relative" aria-hidden="true">
      <div
        className="pointer-events-none absolute left-1/2 top-1/2 h-[70%] w-[80%] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[#D4AF37]/[0.06] blur-[100px]"
        aria-hidden="true"
      />
      <svg viewBox="0 0 440 420" className="relative mx-auto w-full max-w-md">
        <g stroke="rgba(255,255,255,0.03)" strokeWidth="1">
          {[110, 220, 330].map((v) => (
            <g key={v}>
              <line x1={v} y1="0" x2={v} y2="420" />
              <line x1="0" y1={v} x2="440" y2={v} />
            </g>
          ))}
        </g>

        {/* opening node — continuation of the Section 14 transition */}
        <motion.circle
          cx="220" cy="14" r="4" fill="#E7CE8B"
          initial={reduce ? {} : { opacity: 0, scale: 0.3 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.6, ease: EASE, delay: 0.1 }}
          style={{ transformOrigin: "220px 14px" }}
        />
        <DrawLine x1="220" y1="18" x2="220" y2="42" delay={0.2} reduce={reduce} stroke="rgba(212,175,55,0.45)" />

        {/* your enquiry */}
        <motion.rect
          x="150" y="42" width="140" height="36" rx="4"
          fill="rgba(255,255,255,0.03)" stroke="rgba(255,255,255,0.25)" strokeWidth="1"
          initial={reduce ? {} : { opacity: 0, scale: 0.9 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.8, ease: EASE, delay: 0.3 }}
          style={{ transformOrigin: "220px 60px" }}
        />
        <SvgLabel x="220" y="64" size={9.5} delay={0.5} reduce={reduce}>Your Enquiry</SvgLabel>

        {/* channels */}
        {channels.map((c, i) => (
          <g key={c.label}>
            <DrawLine x1="220" y1="78" x2={c.x} y2="138" delay={0.6 + i * 0.18} reduce={reduce} stroke="rgba(255,255,255,0.14)" />
            <GoldDot cx={c.x} cy={142} r={4} delay={0.75 + i * 0.18} reduce={reduce} />
            <SvgLabel x={c.x} y={i % 2 === 0 ? 166 : 184} size={8} delay={0.9 + i * 0.18} reduce={reduce}>
              {c.label}
            </SvgLabel>
            <DrawLine x1={c.x} y1="196" x2="220" y2="238" delay={1.3 + i * 0.18} reduce={reduce} stroke="rgba(212,175,55,0.3)" />
          </g>
        ))}

        {/* logibyte */}
        <motion.rect
          x="135" y="238" width="170" height="44" rx="4"
          fill="rgba(212,175,55,0.09)" stroke="rgba(212,175,55,0.55)" strokeWidth="1"
          initial={reduce ? {} : { opacity: 0, scale: 0.85 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.9, ease: EASE, delay: 1.9 }}
          style={{ transformOrigin: "220px 260px" }}
        />
        <SvgLabel x="220" y="256" gold size={9.5} delay={2.1} reduce={reduce}>Logibyte</SvgLabel>
        <SvgLabel x="220" y="271" gold size={9.5} delay={2.2} reduce={reduce}>Technologies</SvgLabel>

        <DrawLine x1="220" y1="282" x2="220" y2="330" delay={2.4} reduce={reduce} stroke="rgba(212,175,55,0.5)" />
        <GoldDot cx="220" cy="338" r={5} delay={2.55} reduce={reduce} />
        <motion.circle
          cx="220" cy="338" r="14" fill="none" stroke="rgba(212,175,55,0.35)" strokeWidth="1"
          initial={reduce ? {} : { opacity: 0, scale: 0.4 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.7, ease: EASE, delay: 2.65 }}
          style={{ transformOrigin: "220px 338px" }}
        />
        <SvgLabel x="220" y="372" gold size={9} delay={2.8} reduce={reduce}>Appropriate Next Step</SvgLabel>

        <TravelDot points={[[220, 60], [164, 142], [220, 260], [220, 338]]} reduce={reduce} duration={10} />
      </svg>
    </div>
  );
};

export default function Contact() {
  const navigate = useNavigate();
  const reduce = useReducedMotion();
  return (
    <section
      id="connect"
      data-testid="connect-section"
      className="relative scroll-mt-20 overflow-hidden bg-[#0A0906] py-20 sm:py-24"
    >
      <div
        className="pointer-events-none absolute inset-0 bg-[radial-gradient(70%_90%_at_15%_20%,rgba(212,175,55,0.05),transparent_65%)]"
        aria-hidden="true"
      />
      <div className="grain-overlay pointer-events-none absolute inset-0" aria-hidden="true" />

      <div className="relative mx-auto max-w-7xl px-6 lg:px-10">
        <div className="grid items-center gap-14 lg:grid-cols-2 lg:gap-16">
          <div>
            <Reveal>
              <SectionTag index="15">Let&apos;s Connect</SectionTag>
            </Reveal>
            <h2
              data-testid="connect-headline"
              className="mt-6 font-serif text-4xl font-normal leading-[1.08] tracking-tight text-slate-50 sm:text-5xl lg:text-6xl"
            >
              <MaskedLines delay={0.1}>
                Start a{" "}
                <span className="italic text-transparent bg-gradient-to-r from-[#F0DDA6] via-[#E7CE8B] to-[#C9A227] bg-clip-text">
                  Conversation.
                </span>
              </MaskedLines>
            </h2>
            <Reveal delay={0.2}>
              <p className="mt-8 max-w-lg text-[17px] font-normal leading-[1.75] text-stone-300 sm:text-lg">
                Whether you&apos;re exploring a technology requirement, a
                business system, a partnership or a broader corporate
                opportunity, start a conversation with Logibyte Technologies.
              </p>
            </Reveal>
            <Reveal delay={0.3}>
              <p className="mt-5 max-w-lg text-[15px] font-normal leading-[1.75] text-stone-400">
                Share the context and our team will direct the enquiry to the
                appropriate next step.
              </p>
            </Reveal>
            <Reveal delay={0.4}>
              <a
                data-testid="connect-email-link"
                href={`mailto:${CONTACT.email}`}
                className="mt-9 inline-block border-b border-[#D4AF37]/30 pb-1.5 text-lg font-normal tracking-wide text-slate-200 transition-colors duration-500 hover:border-[#E7CE8B]/70 hover:text-white"
              >
                {CONTACT.email}
              </a>
            </Reveal>
          </div>

          <Reveal delay={0.25}>
            <ConnectVisual reduce={reduce} />
          </Reveal>
        </div>

        {/* compact CTA bar */}
        <Reveal delay={0.15}>
          <div
            data-testid="connect-cta-bar"
            className="mt-16 flex flex-col items-center justify-between gap-7 border border-white/10 bg-[#12100A] px-8 py-8 sm:px-12 sm:flex-row"
          >
            <p className="text-xs font-medium uppercase tracking-[0.3em] text-[#E7CE8B]">
              Have Something in Mind?
            </p>
            <div className="flex flex-wrap items-center justify-center gap-6">
              <button
                data-testid="connect-enquiry-btn"
                onClick={() => navigate("/contact")}
                className="group inline-flex items-center gap-3 rounded-full border border-[#D4AF37]/50 px-9 py-3.5 text-xs font-medium uppercase tracking-[0.25em] text-[#E7CE8B] transition-all duration-500 hover:bg-[#D4AF37]/10 hover:shadow-[0_0_28px_-8px_rgba(212,175,55,0.45)]"
              >
                Corporate Enquiries
                <span className="inline-block transition-transform duration-500 group-hover:translate-x-1" aria-hidden="true">
                  →
                </span>
              </button>
              <button
                data-testid="connect-contact-link"
                onClick={() => navigate("/contact")}
                className="group inline-flex items-center gap-2.5 text-xs font-medium uppercase tracking-[0.22em] text-slate-300 transition-colors duration-500 hover:text-[#E7CE8B]"
              >
                <span className="border-b border-current pb-1">Contact Us</span>
                <span className="transition-transform duration-500 group-hover:translate-x-1" aria-hidden="true">
                  →
                </span>
              </button>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
